/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Card, Col, Collapse, Form, Modal, Row, Table } from "react-bootstrap";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createTableBookingLayout,
  fetchTableBookingLayouts,
  updateTableBookingLayout,
} from "../../api/tableBookingApi";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import { resolveCloudinaryImageSrc, uploadFile } from "../../helpers/helper.service";
import { showToast } from "../../store/features/toast/toastSlice";
import SceneHotspotEditor from "./SceneHotspotEditor";
import {
  emptyArea,
  emptyLayoutForm,
  emptySlot,
  emptyTable,
  formStateToPayload,
  getAreaOutlineForDisplay,
  layoutRecordId,
  layoutToFormState,
  normalizeLayoutsList,
  blockWheelChangeOnNumberInput,
  parsePriceFromPerson,
  cacheLayoutAreaOutlines,
  mergeCachedOutlinesIntoLayout,
  syncAreaOutlineAndBounds,
  validateLayoutForm,
  fieldErrorsToMap,
  hasValidZonePrice,
} from "./tableBookingUtils";
import AreaInspectorFields from "./AreaInspectorFields";

const TableBookingLayoutEditor = () => {
  const { t } = useTranslation();
  const { clubId, layoutId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew = layoutId === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyLayoutForm);
  const [clientError, setClientError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [expandedTableAccordion, setExpandedTableAccordion] = useState(null);
  const [apiError, setApiError] = useState("");
  const [focusAreaIndex, setFocusAreaIndex] = useState(0);
  const [focusTableIndex, setFocusTableIndex] = useState(null);
  const [pinNewAreaMode, setPinNewAreaMode] = useState(false);
  const [showHotspotNumbers, setShowHotspotNumbers] = useState(false);
  const [outlineDrawMode, setOutlineDrawMode] = useState(false);
  const [mapExpandedOpen, setMapExpandedOpen] = useState(false);
  const [sceneUploading, setSceneUploading] = useState(false);
  const sceneFileInputRef = useRef(null);
  const [layoutSettingsExpanded, setLayoutSettingsExpanded] = useState(true);
  const [mapShowLabels, setMapShowLabels] = useState(true);
  const [mapShowPrices, setMapShowPrices] = useState(true);
  const [mapShowAvailability, setMapShowAvailability] = useState(true);
  const [mapShowRadius, setMapShowRadius] = useState(true);
  /** Customer-style flow: nested table pins only after clicking the zone on the map (-1 = none expanded). */
  const [mapAreaFirstNested, setMapAreaFirstNested] = useState(true);
  const [mapNestedExpandedIndex, setMapNestedExpandedIndex] = useState(-1);
  const [newAreaModalOpen, setNewAreaModalOpen] = useState(false);
  const [pendingNewPoint, setPendingNewPoint] = useState(null);
  const [newAreaDraft, setNewAreaDraft] = useState({
    label: "",
    code: "",
    priceFrom: "",
  });
  const [newAreaModalErrors, setNewAreaModalErrors] = useState({});

  const resolvedLayoutId = isNew ? null : layoutId;

  useEffect(() => {
    if (isNew) {
      setForm(emptyLayoutForm());
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setApiError("");
      try {
        const res = await fetchTableBookingLayouts(clubId);
        const list = normalizeLayoutsList(res);
        const found = list.find(
          (x) => String(layoutRecordId(x)) === String(layoutId)
        );
        if (cancelled) return;
        if (!found) {
          setApiError(t("TABLE_BOOKING_LAYOUT_NOT_FOUND"));
          setForm(emptyLayoutForm());
        } else {
          const merged = mergeCachedOutlinesIntoLayout(
            found,
            clubId,
            layoutId
          );
          setForm(layoutToFormState(merged));
        }
      } catch (err) {
        if (!cancelled) {
          setApiError(
            typeof err === "string"
              ? err
              : err?.message || t("TABLE_BOOKING_LAYOUTS_LOAD_ERROR")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId, layoutId, isNew]);

  useEffect(() => {
    if (mapAreaFirstNested && focusTableIndex != null) {
      setMapNestedExpandedIndex(focusAreaIndex);
    }
  }, [mapAreaFirstNested, focusTableIndex, focusAreaIndex]);

  const pageTitle = isNew
    ? t("TABLE_BOOKING_LAYOUT_NEW")
    : t("TABLE_BOOKING_LAYOUT_EDIT");

  const clearFieldError = (fieldId) => {
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const focusValidationTarget = (target) => {
    if (!target) return;
    if (target.panel === "settings") {
      setLayoutSettingsExpanded(true);
    }
    if (target.areaIndex != null) {
      setFocusAreaIndex(target.areaIndex);
      setFocusTableIndex(
        target.tableIndex != null ? target.tableIndex : null
      );
      if (target.tableIndex != null) {
        setExpandedTableAccordion(String(target.tableIndex));
      }
    }
  };

  const scrollToValidationField = (target) => {
    if (!target?.fieldId) return;
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        if (target.fieldId === "sceneimageurl") {
          const layoutImage = document.querySelector(
            '[data-field-id="sceneimageurl"]'
          );
          layoutImage?.scrollIntoView({ behavior: "smooth", block: "center" });
          layoutImage?.classList.add("table-booking-panel--field-error");
          window.setTimeout(
            () =>
              layoutImage?.classList.remove("table-booking-panel--field-error"),
            3200
          );
          const urlInput = layoutImage?.querySelector(
            ".table-booking-studio-layout-url-input"
          );
          if (typeof urlInput?.focus === "function") {
            urlInput.focus({ preventScroll: true });
          }
          return;
        }
        if (target.panel === "canvas" || target.fieldId.includes("-hotspot")) {
          const canvas = document.querySelector(
            '[data-panel-id="map-canvas"]'
          );
          canvas?.scrollIntoView({ behavior: "smooth", block: "center" });
          canvas?.classList.add("table-booking-panel--field-error");
          window.setTimeout(
            () => canvas?.classList.remove("table-booking-panel--field-error"),
            3200
          );
          return;
        }
        const el = document.querySelector(
          `[data-field-id="${target.fieldId}"]`
        );
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el?.focus === "function") {
          el.focus({ preventScroll: true });
        }
      }, 120);
    });
  };

  const showValidationFailure = (first, errors) => {
    const map = fieldErrorsToMap(errors || [first]);
    setFieldErrors(map);
    setClientError(first.message);
    setApiError("");
    focusValidationTarget(first);
    scrollToValidationField(first);
    dispatch(
      showToast({
        message: first.message,
        type: "error",
      })
    );
  };

  const onSave = async () => {
    setClientError("");
    setApiError("");
    const result = validateLayoutForm(form, t);
    if (!result.ok) {
      showValidationFailure(result.first, result.errors);
      return;
    }
    setFieldErrors({});
    const payload = formStateToPayload(form);
    setSaving(true);
    try {
      if (isNew) {
        const created = await createTableBookingLayout(clubId, payload);
        const createdId = layoutRecordId(created?.data ?? created);
        cacheLayoutAreaOutlines(clubId, createdId || "new", form.areas);
      } else {
        await updateTableBookingLayout(clubId, resolvedLayoutId, payload);
        cacheLayoutAreaOutlines(clubId, resolvedLayoutId, form.areas);
      }
      dispatch(
        showToast({
          message: t("TABLE_BOOKING_LAYOUT_SAVED"),
          type: "success",
        })
      );
      navigate(`/clubs/${clubId}/table-booking/layouts`);
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.message || t("TABLE_BOOKING_LAYOUT_SAVE_ERROR");
      if (/sceneimageurl/i.test(msg)) {
        showValidationFailure({
          message: t("TABLE_BOOKING_ERR_SCENE_IMAGE"),
          fieldId: "sceneimageurl",
          panel: "layout-image",
        });
      } else {
        setApiError(msg);
        dispatch(
          showToast({
            message: msg,
            type: "error",
          })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const updateSlot = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      slots: prev.slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  };

  const updateArea = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    }));
  };

  const onOutlinePolygonChange = (areaIdx, polygon) => {
    setForm((prev) => {
      const areas = prev.areas.map((a, i) =>
        i === areaIdx ? { ...a, ...syncAreaOutlineAndBounds(a, polygon) } : a
      );
      const cacheId = resolvedLayoutId || "new";
      cacheLayoutAreaOutlines(clubId, cacheId, areas);
      return { ...prev, areas };
    });
  };

  const onHotspotChange = (idx, hotspot) => {
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.map((a, i) =>
        i === idx ? { ...a, hotspot: { ...a.hotspot, ...hotspot } } : a
      ),
    }));
  };

  const onTableHotspotChange = (areaIdx, tableIdx, hotspot) => {
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.map((a, i) => {
        if (i !== areaIdx) return a;
        const tables = [...(a.tables || [])];
        const cur = tables[tableIdx];
        if (!cur) return a;
        tables[tableIdx] = {
          ...cur,
          hotspot: { ...cur.hotspot, ...hotspot },
        };
        return { ...a, tables };
      }),
    }));
  };

  const updateTable = (areaIdx, tableIdx, patch) => {
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.map((a, i) => {
        if (i !== areaIdx) return a;
        const tables = [...(a.tables || [])];
        const cur = tables[tableIdx];
        if (!cur) return a;
        tables[tableIdx] = { ...cur, ...patch };
        return { ...a, tables };
      }),
    }));
  };

  const addTable = (areaIdx) => {
    let newTi = 0;
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.map((a, i) => {
        if (i !== areaIdx) return a;
        const tables = [...(a.tables || []), emptyTable()];
        newTi = tables.length - 1;
        return { ...a, tables };
      }),
    }));
    setFocusAreaIndex(areaIdx);
    setFocusTableIndex(newTi);
  };

  const removeTable = (areaIdx, tableIdx) => {
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.map((a, i) => {
        if (i !== areaIdx) return a;
        const tables = [...(a.tables || [])].filter((_, j) => j !== tableIdx);
        return { ...a, tables };
      }),
    }));
    setFocusTableIndex((ft) => {
      if (ft == null) return null;
      if (ft === tableIdx) return null;
      if (ft > tableIdx) return ft - 1;
      return ft;
    });
  };

  const onRequestAddAtPoint = (p) => {
    setPendingNewPoint(p);
    setNewAreaDraft({
      label: t("TABLE_BOOKING_AREA_NEW_DEFAULT_LABEL", {
        n: form.areas.length + 1,
      }),
      code: "",
      priceFrom: "",
    });
    setNewAreaModalErrors({});
    setNewAreaModalOpen(true);
    setPinNewAreaMode(false);
  };

  const confirmNewAreaModal = () => {
    if (!pendingNewPoint) return;
    const modalErrors = {};
    if (!hasValidZonePrice(newAreaDraft.priceFrom)) {
      modalErrors.priceFrom = t("TABLE_BOOKING_ERR_ZONE_PRICE", {
        row: form.areas.length + 1,
      });
    }
    if (Object.keys(modalErrors).length) {
      setNewAreaModalErrors(modalErrors);
      return;
    }
    const newIdx = form.areas.length;
    setForm((prev) => {
      const na = emptyArea();
      na.hotspot = {
        ...na.hotspot,
        x: pendingNewPoint.x,
        y: pendingNewPoint.y,
      };
      na.label =
        newAreaDraft.label.trim() ||
        t("TABLE_BOOKING_AREA_NEW_DEFAULT_LABEL", {
          n: prev.areas.length + 1,
        });
      na.code = newAreaDraft.code.trim();
      na.priceFromPerPerson = parsePriceFromPerson(newAreaDraft.priceFrom);
      return { ...prev, areas: [...prev.areas, na] };
    });
    setFocusAreaIndex(newIdx);
    setFocusTableIndex(null);
    if (mapAreaFirstNested) setMapNestedExpandedIndex(-1);
    setNewAreaModalOpen(false);
    setPendingNewPoint(null);
    setNewAreaModalErrors({});
  };

  const closeNewAreaModal = () => {
    setNewAreaModalOpen(false);
    setPendingNewPoint(null);
    setNewAreaModalErrors({});
  };

  const handleSceneFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      dispatch(
        showToast({
          message: t("CLUB_LOGO_INVALID_TYPE"),
          type: "error",
        })
      );
      return;
    }
    setSceneUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((p) => ({ ...p, sceneimageurl: url }));
      clearFieldError("sceneimageurl");
      dispatch(
        showToast({
          message: t("TABLE_BOOKING_SCENE_UPLOAD_OK"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          message:
            (typeof err?.message === "string" && err.message) ||
            t("TABLE_BOOKING_SCENE_UPLOAD_ERR"),
          type: "error",
        })
      );
    } finally {
      setSceneUploading(false);
    }
  };

  const previewUrl = useMemo(() => {
    const raw = form.sceneimageurl?.trim() || "";
    if (!raw) return "";
    const base = getCloudinaryMediaBaseUrl();
    return resolveCloudinaryImageSrc(raw, base, "");
  }, [form.sceneimageurl]);

  const focusIdx = focusAreaIndex;
  const focusArea = form.areas[focusIdx];

  const handleStartOutlineDraw = () => {
    const a = form.areas[focusAreaIndex];
    if (!a || !previewUrl) return;
    setPinNewAreaMode(false);
    setOutlineDrawMode((on) => !on);
  };

  const handleClearOutline = () => {
    updateArea(focusAreaIndex, {
      outlinePolygon: null,
      focusBounds: null,
    });
    setOutlineDrawMode(false);
  };

  const outlinePointCount =
    focusArea != null
      ? getAreaOutlineForDisplay(focusArea)?.length ?? 0
      : 0;

  const renderMapEditor = (expanded = false) => (
    <SceneHotspotEditor
      imageUrl={previewUrl}
      areas={form.areas}
      focusIndex={focusAreaIndex}
      focusTableIndex={focusTableIndex}
      onFocusIndex={setFocusAreaIndex}
      onFocusTableIndex={setFocusTableIndex}
      onHotspotChange={onHotspotChange}
      onTableHotspotChange={onTableHotspotChange}
      addAreaOnClick={pinNewAreaMode && !expanded}
      onAddAreaAtPoint={onRequestAddAtPoint}
      showLabels={mapShowLabels}
      showPrices={mapShowPrices}
      useStatusColors={mapShowAvailability}
      showRadiusHandle={mapShowRadius}
      showCoordinateHud={false}
      compactChrome={!expanded}
      showZoomControls
      scrollZoom
      areaFirstNestedTables={mapAreaFirstNested}
      revealNestedTablesAreaIndex={mapNestedExpandedIndex}
      onMapZoneClick={
        mapAreaFirstNested
          ? (ai) => setMapNestedExpandedIndex(ai)
          : undefined
      }
      outlineDrawMode={outlineDrawMode}
      onOutlinePolygonChange={onOutlinePolygonChange}
      showAreaOutlines
    />
  );

  if (loading) {
    return (
      <Card className="mainCard mainCardWrapper">
        <Card.Body>
          <CustomLoadingHTML />
        </Card.Body>
      </Card>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{pageTitle} | RizzTix</title>
      </Helmet>
      <div className="table-booking-studio table-booking-layout-editor">
        <div className="table-booking-studio-topbar">
          <div>
            <h4>{form.title?.trim() || pageTitle}</h4>
            <p className="table-booking-studio-sub">{pageTitle}</p>
          </div>
          <div className="table-booking-studio-actions">
            <Link
              to={`/clubs/${clubId}/table-booking/layouts`}
              className="btn btn-sm btn-studio-secondary"
            >
              {t("TABLE_BOOKING_BACK_LAYOUTS")}
            </Link>
            <button
              type="button"
              className="btn btn-sm btn-studio-primary"
              disabled={saving}
              onClick={onSave}
            >
              {t("SAVE")}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-studio-secondary"
              aria-expanded={layoutSettingsExpanded}
              aria-controls="table-booking-layout-details"
              onClick={() =>
                setLayoutSettingsExpanded((v) => !v)
              }
            >
              {layoutSettingsExpanded
                ? t("TABLE_BOOKING_STUDIO_HIDE_SETTINGS")
                : t("TABLE_BOOKING_STUDIO_LAYOUT_SETTINGS")}
            </button>
          </div>
        </div>
        {clientError ? (
          <Alert variant="warning" className="m-2 mb-0 table-booking-validation-alert">
            <strong>{t("TABLE_BOOKING_VALIDATION_TITLE")}</strong>
            <div>{clientError}</div>
          </Alert>
        ) : null}
        {apiError ? (
          <Alert variant="danger" className="m-2 mb-0">
            {apiError}
          </Alert>
        ) : null}

        <Collapse in={layoutSettingsExpanded}>
          <div
            id="table-booking-layout-details"
            className="table-booking-studio-settings py-3"
          >
            <h2 className="table-booking-studio-settings-title px-2 mb-2">
              {t("TABLE_BOOKING_STUDIO_LAYOUT_SETTINGS")}
            </h2>
            <Row className="g-3 px-2">
              <Col md={6} lg={3}>
                <Form.Group
                  className={
                    fieldErrors.viewkey ? "table-booking-field-error-group" : ""
                  }
                >
                  <Form.Label>{t("TABLE_BOOKING_VIEWKEY")}</Form.Label>
                  <Form.Control
                    data-field-id="viewkey"
                    isInvalid={Boolean(fieldErrors.viewkey)}
                    value={form.viewkey}
                    onChange={(e) => {
                      clearFieldError("viewkey");
                      setForm((p) => ({
                        ...p,
                        viewkey: e.target.value.toLowerCase(),
                      }));
                    }}
                    placeholder="main-floor"
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.viewkey}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} lg={3}>
                <Form.Group
                  className={
                    fieldErrors.title ? "table-booking-field-error-group" : ""
                  }
                >
                  <Form.Label>{t("TABLE_BOOKING_LAYOUT_TITLE")}</Form.Label>
                  <Form.Control
                    data-field-id="title"
                    isInvalid={Boolean(fieldErrors.title)}
                    value={form.title}
                    onChange={(e) => {
                      clearFieldError("title");
                      setForm((p) => ({ ...p, title: e.target.value }));
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>{t("TABLE_BOOKING_TIMEZONE")}</Form.Label>
                  <Form.Control
                    value={form.timezone}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, timezone: e.target.value }))
                    }
                    placeholder="Asia/Kolkata"
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>{t("TABLE_BOOKING_SORTORDER")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.sortorder}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        sortorder: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="px-2 pt-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <strong className="small">{t("TABLE_BOOKING_SECTION_SLOTS")}</strong>
              <button
                type="button"
                className="btn btn-sm btn-studio-secondary"
                onClick={() =>
                  setForm((p) => ({ ...p, slots: [...p.slots, emptySlot()] }))
                }
              >
                {t("TABLE_BOOKING_ADD_SLOT")}
              </button>
            </div>
            <div className="table-booking-studio-settings-slots-wrap px-2 mt-2">
            <Table responsive className="mb-0 align-middle small table-booking-slots-table">
              <thead>
                <tr>
                  <th>{t("TABLE_BOOKING_SLOT_KEY")}</th>
                  <th>{t("TABLE_BOOKING_SLOT_LABEL")}</th>
                  <th>{t("TABLE_BOOKING_SLOT_START")}</th>
                  <th>{t("TABLE_BOOKING_SLOT_END")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {form.slots.map((slot, sidx) => (
                  <tr key={slot.key}>
                    <td>
                      <Form.Control
                        size="sm"
                        value={slot.key}
                        onChange={(e) =>
                          updateSlot(sidx, { key: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={slot.label}
                        onChange={(e) =>
                          updateSlot(sidx, { label: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        data-field-id={`slot-${sidx}-startTime`}
                        isInvalid={Boolean(
                          fieldErrors[`slot-${sidx}-startTime`]
                        )}
                        value={slot.startTime}
                        onChange={(e) => {
                          clearFieldError(`slot-${sidx}-startTime`);
                          updateSlot(sidx, { startTime: e.target.value });
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        data-field-id={`slot-${sidx}-endTime`}
                        isInvalid={Boolean(
                          fieldErrors[`slot-${sidx}-endTime`]
                        )}
                        value={slot.endTime}
                        onChange={(e) => {
                          clearFieldError(`slot-${sidx}-endTime`);
                          updateSlot(sidx, { endTime: e.target.value });
                        }}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        disabled={form.slots.length <= 1}
                        onClick={() =>
                          setForm((p) => ({
                            ...p,
                            slots: p.slots.filter((_, i) => i !== sidx),
                          }))
                        }
                      >
                        {t("DELETE")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          </div>
        </Collapse>

        <div className="table-booking-studio-legend px-2">
          <span className="lg-a">
            <i aria-hidden /> {t("TABLE_BOOKING_LEGEND_AVAILABLE")}
          </span>
          <span className="lg-s">
            <i aria-hidden /> {t("TABLE_BOOKING_LEGEND_SOLD_OUT")}
          </span>
          <span className="lg-u">
            <i aria-hidden /> {t("TABLE_BOOKING_LEGEND_UNAVAILABLE")}
          </span>
        </div>

        <div className="table-booking-studio-grid">
          <aside className="table-booking-studio-panel">
            <div className="table-booking-studio-panel-hd">
              {t("TABLE_BOOKING_STUDIO_AREAS")}
            </div>
            <div className="table-booking-studio-areas-scroll">
              <button
                type="button"
                className="table-booking-studio-tool-btn w-100 mb-2 justify-content-center"
                onClick={() => {
                  setForm((p) => {
                    const nextAreas = [...p.areas, emptyArea()];
                    setFocusAreaIndex(nextAreas.length - 1);
                    setFocusTableIndex(null);
                    return { ...p, areas: nextAreas };
                  });
                }}
              >
                + {t("TABLE_BOOKING_ADD_AREA")}
              </button>
              {form.areas.map((ar, idx) => {
                const areaHasErrors = Object.keys(fieldErrors).some((key) =>
                  key.startsWith(`area-${idx}-`)
                );
                const avail = String(
                  ar.manualAvailability || "AUTO"
                ).toUpperCase();
                let badgeClass = "table-booking-studio-badge--auto";
                if (avail === "SOLD_OUT")
                  badgeClass = "table-booking-studio-badge--sold";
                if (avail === "UNAVAILABLE")
                  badgeClass = "table-booking-studio-badge--unavail";
                const priceHint =
                  ar.priceDisplayLabel?.trim() ||
                  `${ar.currency || ""} ${ar.priceFromPerPerson ?? ""} / pax`.trim();
                return (
                  <div key={`area-row-${idx}`} className="position-relative">
                    <button
                      type="button"
                      className={`table-booking-studio-area-row ${
                        focusIdx === idx ? "is-active" : ""
                      } ${areaHasErrors ? "has-field-error" : ""}`}
                      onClick={() => {
                        setFocusAreaIndex(idx);
                        setFocusTableIndex(null);
                        setOutlineDrawMode(false);
                        if (mapAreaFirstNested) setMapNestedExpandedIndex(-1);
                      }}
                    >
                      <span className="table-booking-studio-area-row-name">
                        {ar.label?.trim() ||
                          t("TABLE_BOOKING_AREA_ROW", { n: idx + 1 })}
                      </span>
                      <span className="table-booking-studio-area-row-meta">
                        {priceHint} · {ar.minPartySize}–{ar.maxPartySize}{" "}
                        {t("TABLE_BOOKING_STUDIO_GUESTS")}
                      </span>
                      <span
                        className={`table-booking-studio-badge ${badgeClass}`}
                      >
                        {avail.replace("_", " ")}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0 position-absolute"
                      style={{ top: 6, right: 6 }}
                      disabled={form.areas.length <= 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        setForm((p) => {
                          const next = p.areas.filter((_, i) => i !== idx);
                          setFocusAreaIndex((fi) => {
                            if (next.length === 0) return 0;
                            if (fi === idx) return Math.max(0, idx - 1);
                            if (fi > idx) return fi - 1;
                            return fi;
                          });
                          setFocusTableIndex(null);
                          return { ...p, areas: next };
                        });
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </aside>

          <section
            className="table-booking-studio-panel table-booking-studio-panel--canvas"
            data-panel-id="map-canvas"
          >
            <div className="table-booking-studio-toolbar">
              <button
                type="button"
                className={`table-booking-studio-tool-btn ${
                  !pinNewAreaMode ? "is-active" : ""
                }`}
                onClick={() => setPinNewAreaMode(false)}
              >
                {t("TABLE_BOOKING_STUDIO_TOOL_SELECT")}
              </button>
              <button
                type="button"
                className={`table-booking-studio-tool-btn ${
                  pinNewAreaMode ? "is-active" : ""
                }`}
                disabled={!previewUrl}
                title={
                  !previewUrl
                    ? t("TABLE_BOOKING_PIN_REQUIRES_SCENE")
                    : undefined
                }
                onClick={() => setPinNewAreaMode(true)}
              >
                + {t("TABLE_BOOKING_PIN_NEW_ON_MAP")}
              </button>
              <div className="table-booking-studio-map-toggles">
                <label>
                  <input
                    type="checkbox"
                    checked={mapShowLabels}
                    onChange={(e) => setMapShowLabels(e.target.checked)}
                  />
                  {t("TABLE_BOOKING_MAP_TOGGLE_LABELS")}
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={mapShowPrices}
                    onChange={(e) => setMapShowPrices(e.target.checked)}
                  />
                  {t("TABLE_BOOKING_MAP_TOGGLE_PRICES")}
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={mapShowAvailability}
                    onChange={(e) =>
                      setMapShowAvailability(e.target.checked)
                    }
                  />
                  {t("TABLE_BOOKING_MAP_TOGGLE_AVAIL")}
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={mapShowRadius}
                    onChange={(e) => setMapShowRadius(e.target.checked)}
                  />
                  {t("TABLE_BOOKING_MAP_TOGGLE_RADIUS")}
                </label>
                <label
                  className="text-nowrap"
                  title={t("TABLE_BOOKING_MAP_AREA_FIRST_HINT")}
                >
                  <input
                    type="checkbox"
                    checked={mapAreaFirstNested}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setMapAreaFirstNested(on);
                      if (on) setMapNestedExpandedIndex(-1);
                    }}
                  />
                  {t("TABLE_BOOKING_MAP_AREA_FIRST")}
                </label>
              </div>
            </div>
            <div
              className={`table-booking-studio-layout-image-bar ${
                fieldErrors.sceneimageurl ? "has-field-error" : ""
              }`}
              data-field-id="sceneimageurl"
            >
              <div className="table-booking-studio-layout-image-label">
                {t("TABLE_BOOKING_CANVAS_LAYOUT_IMAGE")}{" "}
                <span className="text-danger">*</span>
              </div>
              <div className="table-booking-studio-layout-image-row">
                <input
                  ref={sceneFileInputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleSceneFileChange}
                  disabled={sceneUploading}
                />
                <button
                  type="button"
                  className={`table-booking-studio-tool-btn text-nowrap ${
                    fieldErrors.sceneimageurl ? "is-invalid-choice" : ""
                  }`}
                  disabled={sceneUploading}
                  onClick={() => sceneFileInputRef.current?.click()}
                >
                  {sceneUploading
                    ? t("TABLE_BOOKING_SCENE_UPLOADING")
                    : t("TABLE_BOOKING_CHOOSE_LAYOUT_IMAGE")}
                </button>
                <Form.Control
                  size="sm"
                  className="table-booking-studio-layout-url-input"
                  isInvalid={Boolean(fieldErrors.sceneimageurl)}
                  value={form.sceneimageurl}
                  onChange={(e) => {
                    clearFieldError("sceneimageurl");
                    setForm((p) => ({
                      ...p,
                      sceneimageurl: e.target.value,
                    }));
                  }}
                  placeholder={t("TABLE_BOOKING_SCENE_URL_PLACEHOLDER")}
                />
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt=""
                    className="table-booking-studio-layout-thumb"
                  />
                ) : null}
              </div>
              {fieldErrors.sceneimageurl ? (
                <p className="table-booking-layout-image-error mb-1">
                  {fieldErrors.sceneimageurl}
                </p>
              ) : null}
              <p className="table-booking-studio-layout-hint mb-0">
                {t("TABLE_BOOKING_CANVAS_LAYOUT_HINT")}
              </p>
            </div>
            {pinNewAreaMode ? (
              <Alert
                variant="info"
                className="mx-2 mt-2 py-2 mb-0 small d-flex flex-wrap justify-content-between gap-2 align-items-center"
              >
                <span>{t("TABLE_BOOKING_PIN_MODE_BANNER")}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-studio-secondary"
                  onClick={() => setPinNewAreaMode(false)}
                >
                  {t("TABLE_BOOKING_CANCEL_PIN_MODE")}
                </button>
              </Alert>
            ) : null}
            {outlineDrawMode ? (
              <Alert
                variant="info"
                className="mx-2 mt-2 py-2 mb-0 small d-flex flex-wrap justify-content-between gap-2 align-items-center"
              >
                <span>{t("TABLE_BOOKING_OUTLINE_MODE_BANNER")}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-studio-secondary"
                  onClick={() => setOutlineDrawMode(false)}
                >
                  {t("TABLE_BOOKING_OUTLINE_DONE")}
                </button>
              </Alert>
            ) : null}
            <div className="table-booking-studio-canvas-inner">
              {renderMapEditor(false)}
            </div>
          </section>

          <aside className="table-booking-studio-panel table-booking-studio-panel--inspector">
            <div className="table-booking-studio-panel-hd">
              {t("TABLE_BOOKING_STUDIO_INSPECTOR")}
            </div>
            <div className="table-booking-studio-panel-body">
              <AreaInspectorFields
                t={t}
                index={focusIdx}
                area={focusArea}
                updateArea={updateArea}
                onHotspotChange={onHotspotChange}
                showHotspotNumbers={showHotspotNumbers}
                setShowHotspotNumbers={setShowHotspotNumbers}
                focusTableIndex={focusTableIndex}
                onFocusTableIndex={setFocusTableIndex}
                updateTable={updateTable}
                addTable={addTable}
                removeTable={removeTable}
                outlineDrawMode={outlineDrawMode}
                outlinePointCount={outlinePointCount}
                onStartOutlineDraw={handleStartOutlineDraw}
                onClearOutline={handleClearOutline}
                fieldErrors={fieldErrors}
                onClearFieldError={clearFieldError}
                expandedTableAccordion={expandedTableAccordion}
                onExpandedTableAccordionChange={setExpandedTableAccordion}
                onExpandMap={() => {
                  setMapExpandedOpen(true);
                  setPinNewAreaMode(false);
                }}
                hasSceneImage={Boolean(previewUrl)}
              />
            </div>
          </aside>
        </div>
      </div>

      <Modal
        show={mapExpandedOpen}
        onHide={() => setMapExpandedOpen(false)}
        fullscreen
        contentClassName="table-booking-expand-map-modal"
      >
        <Modal.Header closeButton className="border-secondary">
          <Modal.Title>{t("TABLE_BOOKING_EXPAND_MAP_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="table-booking-expand-map-body p-2">
          {outlineDrawMode ? (
            <Alert variant="info" className="py-2 small mb-2">
              {t("TABLE_BOOKING_OUTLINE_MODE_BANNER")}
            </Alert>
          ) : null}
          <div className="table-booking-expand-map-canvas">
            {renderMapEditor(true)}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setMapExpandedOpen(false)}
          >
            {t("TABLE_BOOKING_EXPAND_MAP_DONE")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={newAreaModalOpen}
        onHide={closeNewAreaModal}
        centered
        contentClassName="table-booking-studio-modal-content"
      >
        <Modal.Header closeButton className="border-secondary">
          <Modal.Title>{t("TABLE_BOOKING_NEW_AREA_MODAL_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>{t("TABLE_BOOKING_AREA_LABEL")}</Form.Label>
            <Form.Control
              value={newAreaDraft.label}
              onChange={(e) =>
                setNewAreaDraft((d) => ({ ...d, label: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>{t("TABLE_BOOKING_AREA_CODE")}</Form.Label>
            <Form.Control
              value={newAreaDraft.code}
              onChange={(e) =>
                setNewAreaDraft((d) => ({ ...d, code: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>
              {t("TABLE_BOOKING_PRICE_FROM_PERSON")}{" "}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              isInvalid={Boolean(newAreaModalErrors.priceFrom)}
              value={newAreaDraft.priceFrom}
              onWheel={blockWheelChangeOnNumberInput}
              onChange={(e) => {
                setNewAreaModalErrors((prev) => {
                  if (!prev.priceFrom) return prev;
                  const next = { ...prev };
                  delete next.priceFrom;
                  return next;
                });
                setNewAreaDraft((d) => ({
                  ...d,
                  priceFrom: e.target.value,
                }));
              }}
            />
            <Form.Control.Feedback type="invalid">
              {newAreaModalErrors.priceFrom}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeNewAreaModal}
          >
            {t("CANCEL")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirmNewAreaModal}
          >
            {t("TABLE_BOOKING_NEW_AREA_MODAL_ADD")}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default TableBookingLayoutEditor;
